/* eslint-disable @typescript-eslint/no-explicit-any */
import { css, cx } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import type { TaskStatusSize } from "@octopusdeploy/design-system-octopus-components";
import { TaskStatus } from "@octopusdeploy/design-system-octopus-components";
import type { InterruptionType } from "@octopusdeploy/octopus-server-client";
import { TaskState } from "@octopusdeploy/octopus-server-client";
import moment from "moment";
import type { ReactNode } from "react";
import { getPrimaryPendingInterruptionType } from "~/areas/projects/components/TaskStatusIcon/getPrimaryPendingInterruptionType";
import type { OctopusTheme } from "~/components/Theme";
import { useOctopusTheme } from "~/components/Theme";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import iconStyle from "./iconStyle";
type TaskStatusIconSize = 20 | 28 | 32 | 40 | 48;
interface TaskStatusIconProps {
    item: TaskStatusIconPropsItem;
    size?: TaskStatusIconSize;
    iconOnly?: boolean;
    iconClassName?: string;
}
interface TaskStatusIconPropsItem {
    PendingInterruptionTypes: InterruptionType[];
    HasWarningsOrErrors: boolean;
    IsCompleted: boolean;
    LastUpdatedTime?: string;
    StartTime?: string | null;
    QueueTime?: string;
    QueueTimeExpiry?: string;
    Duration?: string;
    ErrorMessage?: string;
    State: TaskState;
}
function momentFromFilter(fromDate: any, toDate: any) {
    if (!fromDate) {
        return null;
    }
    const fromMoment = moment(fromDate);
    const toMoment = toDate ? moment(toDate) : moment();
    const diff = fromMoment.diff(toMoment);
    const duration = moment.duration(diff);
    const mins = duration.minutes();
    const hours = duration.hours();
    const days = Math.floor(duration.asDays());
    let result = mins === 1 ? "1 minute" : mins + " minutes";
    result = (hours !== 0 ? (hours === 1 ? "1 hour" : hours + " hours") + " and " : "") + result;
    result = (days !== 0 ? (days === 1 ? "1 day" : days + " days") + ", " : "") + result;
    return result;
}
function inspector(task: TaskStatusIconPropsItem, theme: OctopusTheme, useNewIcons: boolean, newIconSize: TaskStatusSize, iconOnly: boolean) {
    const pendingInterruptionTypes = task.PendingInterruptionTypes;
    const warningsOrErrors = task.HasWarningsOrErrors;
    const optionalDuration = task.Duration ? ` Duration: ${task.Duration}` : "";
    const errorMessage = task.ErrorMessage;
    let description: string = task.State;
    let tip = "";
    let html: ReactNode = useNewIcons ? <TaskStatus status="queued" size={newIconSize}/> : <em />;
    const tooltip = (t: string) => (iconOnly ? undefined : t);
    if (pendingInterruptionTypes.length > 0 && !task.IsCompleted) {
        description = "Waiting...";
        ({ tip, html } = getInterruptionTooltipAndIcon(pendingInterruptionTypes, optionalDuration, useNewIcons, newIconSize, tooltip));
    }
    else if (task.State === TaskState.Cancelling) {
        description = "Cancelling";
        tip = "Cancellation has been requested, attempting to stop the task.";
        html = useNewIcons ? (<TaskStatus status="canceling" size={newIconSize} tooltip={tooltip(tip)}/>) : (<span style={{ position: "relative" }}>
                <em className="fa-solid fa-user" style={{ marginRight: "0.4rem" }}/>
                <span className="status-overlay" style={{ position: "absolute", bottom: "-0.15rem", right: "-0.1rem", fontSize: "12px", borderRadius: "15px", backgroundColor: theme.darkBackground, width: "13px", height: "13px", marginTop: "0px" }}>
                    <em className="fa-solid fa-spinner fa-pulse" style={{ marginTop: "1px", position: "absolute", marginLeft: "1px" }}/>
                </span>
            </span>);
    }
    else if (task.State === TaskState.Queued) {
        description = "Queued...";
        const timeQueued = moment(task.QueueTime).format("dddd");
        tip = `Queued for ${timeQueued}`;
        html = useNewIcons ? <TaskStatus status="queued" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-regular fa-clock"/>;
    }
    else if (task.State === TaskState.Executing) {
        tip = `This task is being executed by the Octopus Server.${optionalDuration}`;
        html = useNewIcons ? <TaskStatus status="executing" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-spinner fa-pulse"/>;
    }
    else if (task.State === TaskState.Failed) {
        tip = "An error occurred while processing this task";
        tip = errorMessage && errorMessage.length > 0 ? `${tip}: ${errorMessage}` : `${tip}. See the task details for more information.`;
        html = useNewIcons ? <TaskStatus status="failed" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-xmark"/>;
    }
    else if (task.State === TaskState.Canceled) {
        tip = "This task was canceled.";
        tip = errorMessage && errorMessage.length > 0 ? `${tip} ${errorMessage}` : tip;
        html = useNewIcons ? <TaskStatus status="canceled" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-user-times"/>;
    }
    else if (task.State === TaskState.TimedOut) {
        description = "Timed out";
        const queueTime = moment(task.QueueTime).calendar();
        const expiryTime = momentFromFilter(task.QueueTimeExpiry, task.QueueTime);
        const lastUpdated = moment(task.LastUpdatedTime).calendar();
        tip = `The task was scheduled to run ${queueTime} within ${expiryTime} but the Octopus Server attempted to run the task ${lastUpdated}. This can happen when the Octopus Server is too busy to process the task.`;
        html = useNewIcons ? <TaskStatus status="timed-out" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-regular fa-clock"/>;
    }
    else if (task.State === TaskState.Success) {
        if (warningsOrErrors) {
            tip = `This task completed successfully, but warnings or errors were reported in the log.${optionalDuration}`;
            html = useNewIcons ? (<TaskStatus status="success-with-warnings" size={newIconSize} tooltip={tooltip(tip)}/>) : (<span style={{ position: "relative" }}>
                    <em className="fa-solid fa-check"/>
                    <span className="status-overlay" style={{ position: "absolute", bottom: "-8px", right: "-0.0625rem", fontSize: "10px" }}>
                        <em className="fa-solid fa-exclamation-triangle"/>
                    </span>
                </span>);
        }
        else {
            tip = `This task completed without any problems.${optionalDuration}`;
            html = useNewIcons ? <TaskStatus status="success" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-check"/>;
        }
    }
    return {
        tip,
        html,
        description,
    };
}
function getInterruptionTooltipAndIcon(interruptionTypes: InterruptionType[], optionalDuration: string, useNewIcons: boolean, newIconSize: TaskStatusSize, tooltip: (t: string) => string | undefined): {
    tip: string;
    html: ReactNode;
} {
    let tip: string = "";
    const primaryInterruptionType = getPrimaryPendingInterruptionType(interruptionTypes);
    if (interruptionTypes.length > 1) {
        tip = `Task is waiting on ${interruptionTypes.length} interruptions before it continues.${optionalDuration}`;
    }
    else {
        switch (primaryInterruptionType) {
            case "ManualIntervention":
                tip = `Task is waiting for manual intervention.${optionalDuration}`;
                break;
            case "PullRequestCompletion":
                tip = `Task is waiting for pull requests to be completed.${optionalDuration}`;
                break;
            case "ArgoCDApplicationSync":
                tip = `Task is waiting for Argo CD applications to become synced.${optionalDuration}`;
                break;
        }
    }
    let icon: ReactNode = <></>;
    switch (primaryInterruptionType) {
        case "ManualIntervention":
            icon = useNewIcons ? <TaskStatus status="pending-intervention" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-eye"/>;
            break;
        case "PullRequestCompletion":
            icon = useNewIcons ? <TaskStatus status="pending-pull-request-completion" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-code-pull-request"/>;
            break;
        case "ArgoCDApplicationSync":
            //Argo CD Application Sync is treated like a running task (as the user can't intervene)
            icon = useNewIcons ? <TaskStatus status="executing" size={newIconSize} tooltip={tooltip(tip)}/> : <em className="fa-solid fa-spinner fa-pulse"/>;
            break;
    }
    return { tip, html: icon };
}
const TaskStatusIcon: React.FC<TaskStatusIconProps> = ({ item, iconOnly, iconClassName, size = 32 }) => {
    const isNewStatusIconsFeatureEnabled = useOctopusFeatureToggle("new-status-icons", false);
    const theme = useOctopusTheme();
    const newIconSize = mapToNewIconSize(size);
    const { tip, html } = inspector(item, theme, isNewStatusIconsFeatureEnabled, newIconSize, !!iconOnly);
    if (isNewStatusIconsFeatureEnabled) {
        return html;
    }
    const oldSize = toOldTaskStatusIconSize(size);
    const icon = (<span role="img" className={cx(iconStyle(item, iconClassName), oldTaskStatusIconSizeStyles[oldSize])}>
            {html}
        </span>);
    if (iconOnly) {
        return icon;
    }
    return <Tooltip content={tip}>{icon}</Tooltip>;
};
TaskStatusIcon.displayName = "TaskStatusIcon"
TaskStatusIcon.displayName = "TaskStatusIcon";
export { TaskStatusIcon, iconStyle, TaskStatusIconPropsItem };
function mapToNewIconSize(size: TaskStatusIconSize): TaskStatusSize {
    if (size === 20)
        return 28;
    if (size === 48)
        return 40;
    return size;
}
function toOldTaskStatusIconSize(size: TaskStatusIconSize) {
    if (size === 40)
        return 48;
    return size;
}
const oldTaskStatusIconSizeStyles: Record<20 | 28 | 32 | 48, string> = {
    20: css({
        width: "1.25rem",
        height: "1.25rem",
        fontSize: "0.875rem",
    }),
    28: css({
        width: "1.75rem",
        height: "1.75rem",
        fontSize: "1rem",
    }),
    32: css({
        width: "2rem",
        height: "2rem",
        fontSize: "1.25rem",
    }),
    48: css({
        width: "3rem",
        height: "3rem",
        fontSize: "1.75rem",
    }),
};
